import { useState, useEffect, useCallback } from "react";
import {
  Check, CheckCircle2, FileText, Upload, X, Plus, Headset, LayoutDashboard,
  UserRound, BadgeCheck, Languages as LanguagesIcon, GraduationCap, Mail, Phone,
  UserPlus, Stethoscope, CalendarPlus, MessageSquare, KeyRound, Copy, ShieldCheck,
  ShieldAlert, RefreshCw, Lock, IdCard, Search, ArrowLeft, ArrowRight, Clock,
  Calendar as CalIcon, Inbox as InboxIcon, PhoneCall, Megaphone, CheckCheck,
  SendHorizonal, Ticket, ShieldQuestion, Send, AtSign,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";
import { toast } from "@/lib/toast";
import PortalShell from "@/components/portal/PortalShell";
import Calendar from "@/components/portal/Calendar";
import Inbox from "@/components/portal/Inbox";
import { TicketThread } from "@/components/portal/AdminTicketInbox";
import { COMPLAINTS, NEEDS } from "@/data/content";

const SEX_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];
const DOC_SPECIALTIES = ["Oncology", "Cardiology", "Neurology", "Orthopaedics", "Nephrology",
  "Gastroenterology", "Endocrinology", "Pulmonology", "OBGYN", "Dermatology", "Ophthalmology"];

/* ----------------------------- Support Auth ----------------------------- */
function SupportAuth({ onDone }) {
  const { refresh } = useAuth();
  const [mode, setMode] = useState("login");
  const [stage, setStage] = useState("form"); // form | otp | docs | pending
  const [form, setForm] = useState({
    name: "", age: "", sex: "", qualification: "", email: "", mobile: "", password: "",
  });
  const [languages, setLanguages] = useState([]);
  const [langInput, setLangInput] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [devEmail, setDevEmail] = useState("");
  const [devPhone, setDevPhone] = useState("");
  const [uploadToken, setUploadToken] = useState("");
  const [files, setFiles] = useState({});
  const [employeeNo, setEmployeeNo] = useState("");
  const [newPass, setNewPass] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addLang = () => {
    const v = langInput.trim();
    if (v && !languages.some((l) => l.toLowerCase() === v.toLowerCase())) {
      setLanguages([...languages, v]);
    }
    setLangInput("");
  };

  const login = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/login", { email: form.email, password: form.password });
      const u = await refresh();
      if (u?.role !== "support") { setErr("This is the customer support portal."); return; }
      onDone();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendReset = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/support/forgot-otp", { email: form.email });
      if (data.dev_email_otp) setDevEmail(data.dev_email_otp);
      if (data.dev_phone_otp) setDevPhone(data.dev_phone_otp);
      setStage("otp");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyReset = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/support/reset-password", {
        email: form.email, email_code: emailCode, phone_code: phoneCode, new_password: newPass,
      });
      toast("Password updated — please sign in.");
      setMode("login"); setStage("form"); setEmailCode(""); setPhoneCode(""); setNewPass(""); setDevEmail(""); setDevPhone("");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/support/send-otp", { ...form, languages });
      if (data.dev_email_otp) setDevEmail(data.dev_email_otp);
      if (data.dev_phone_otp) setDevPhone(data.dev_phone_otp);
      setStage("otp");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/support/verify-otp", {
        email: form.email, email_code: emailCode, phone_code: phoneCode,
      });
      setUploadToken(data.upload_token);
      setEmployeeNo(data.employee_no);
      setStage("docs");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitDocs = async () => {
    setErr(""); setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(files).forEach(([k, f]) => { if (f) { fd.append("files", f); fd.append("types", k); } });
      await api.post("/support/documents", fd, { headers: { Authorization: `Bearer ${uploadToken}` } });
      setStage("pending");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (stage === "pending") return (
    <div className="card" style={{ maxWidth: 540, margin: "30px auto", textAlign: "center" }}>
      <CheckCircle2 size={42} color="#1D9E75" />
      <h2 className="h" style={{ fontSize: 22 }}>Registration submitted</h2>
      <p className="lead">Your employee number is <b>{employeeNo}</b>. Our admin team will review your details and approve your account. You’ll be able to sign in once approved.</p>
      <button className="btn btn-ink" onClick={() => { setMode("login"); setStage("form"); }} data-testid="support-back-signin">Back to sign in</button>
    </div>
  );

  return (
    <div className="auth">
      <div className="auth-art support">
        <div className="logo" style={{ marginBottom: 22 }}><Headset size={26} /></div>
        <h2>Help patients find clarity. Join our customer support team.</h2>
        <p>Guide patients and doctors through the platform with empathy and care.</p>
        {["Verified support specialists", "Secure, compliant workspace", "Make every interaction count"].map((t) => (
          <div className="pt" key={t}><Check size={16} /> {t}</div>
        ))}
      </div>

      <div className="auth-form">
        <div className="tabs">
          <button className={`tab ${mode === "login" ? "on" : ""}`} onClick={() => { setMode("login"); setStage("form"); setErr(""); }} data-testid="support-tab-login">Sign in</button>
          <button className={`tab ${mode === "register" ? "on" : ""}`} onClick={() => { setMode("register"); setStage("form"); setErr(""); }} data-testid="support-tab-register">Register</button>
        </div>
        {err && <p className="err" data-testid="support-auth-error">{err}</p>}

        {mode === "login" && (
          <>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="support-login-email" /></div>
            <div className="field"><label>Password</label><input className="in" type="password" value={form.password} onChange={set("password")} data-testid="support-login-password" /></div>
            <button className="btn btn-ink full" onClick={login} disabled={busy} data-testid="support-login-submit">{busy ? "Signing in…" : "Sign in"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => { setMode("forgot"); setStage("form"); setErr(""); }} data-testid="support-forgot-link">Forgot your password?</p>
          </>
        )}

        {mode === "forgot" && stage === "form" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter your registered email. We’ll send verification codes to your email and phone.</p>
            <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="support-forgot-email" /></div>
            <button className="btn btn-ink full" onClick={sendReset} disabled={busy} data-testid="support-forgot-submit">{busy ? "Sending codes…" : "Send codes"}</button>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => { setMode("login"); setStage("form"); setErr(""); }}>Back to sign in</p>
          </>
        )}

        {mode === "forgot" && stage === "otp" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Enter the codes sent to your email (<b>{form.email}</b>) and phone, then choose a new password.</p>
            {(devEmail || devPhone) && (
              <div className="demohint">Demo mode — email code <b>{devEmail || "—"}</b>, phone code <b>{devPhone || "—"}</b></div>
            )}
            <div className="field"><label><Mail size={13} /> Email OTP</label><input className="in" maxLength={6} value={emailCode} onChange={(e) => setEmailCode(e.target.value)} data-testid="support-reset-email-otp" /></div>
            <div className="field"><label><Phone size={13} /> Phone OTP</label><input className="in" maxLength={6} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} data-testid="support-reset-phone-otp" /></div>
            <div className="field"><label>New password (min 8)</label><input className="in" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="support-reset-newpass" /></div>
            <button className="btn btn-green full" onClick={verifyReset} disabled={busy} data-testid="support-reset-verify">{busy ? "Updating…" : "Update password"}</button>
          </>
        )}

        {mode === "register" && stage === "form" && (
          <>
            <div className="field"><label>Name (as per Aadhaar)</label><input className="in" value={form.name} onChange={set("name")} data-testid="support-name" /></div>
            <div className="row2">
              <div className="field"><label>Age</label><input className="in" value={form.age} onChange={set("age")} data-testid="support-age" /></div>
              <div className="field"><label>Gender</label>
                <select className="in" value={form.sex} onChange={set("sex")} data-testid="support-sex">
                  <option value="">Select</option>{SEX_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="field"><label>Qualification</label><input className="in" value={form.qualification} onChange={set("qualification")} placeholder="e.g. B.A. Psychology" data-testid="support-qualification" /></div>

            <div className="field">
              <label>Languages known</label>
              <div className="lang-add">
                <input
                  className="in" value={langInput}
                  onChange={(e) => setLangInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLang())}
                  placeholder="Type a language and press Add"
                  data-testid="support-lang-input"
                />
                <button type="button" className="btn btn-ghost btn-sm" onClick={addLang} data-testid="support-lang-add"><Plus size={14} /> Add</button>
              </div>
              {languages.length > 0 && (
                <div className="lang-chips" data-testid="support-lang-chips">
                  {languages.map((l) => (
                    <span className="lang-chip" key={l}>{l}
                      <button type="button" onClick={() => setLanguages(languages.filter((x) => x !== l))}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="row2">
              <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} data-testid="support-email" /></div>
              <div className="field"><label>Phone number</label><input className="in" value={form.mobile} onChange={set("mobile")} placeholder="+91…" data-testid="support-mobile" /></div>
            </div>
            <div className="field"><label>Password (min 8)</label><input className="in" type="password" value={form.password} onChange={set("password")} data-testid="support-password" /></div>
            <button className="btn btn-green full" onClick={sendOtp} disabled={busy} data-testid="support-reg-submit">{busy ? "Sending codes…" : "Verify email & phone"}</button>
          </>
        )}

        {mode === "register" && stage === "otp" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>We’ve sent codes to your email (<b>{form.email}</b>) and phone (<b>{form.mobile}</b>). Enter both to continue.</p>
            {(devEmail || devPhone) && (
              <div className="demohint">Demo mode — email code <b>{devEmail || "—"}</b>, phone code <b>{devPhone || "—"}</b></div>
            )}
            <div className="field"><label><Mail size={13} /> Email OTP</label><input className="in" maxLength={6} value={emailCode} onChange={(e) => setEmailCode(e.target.value)} data-testid="support-email-otp" /></div>
            <div className="field"><label><Phone size={13} /> Phone OTP</label><input className="in" maxLength={6} value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} data-testid="support-phone-otp" /></div>
            <button className="btn btn-green full" onClick={verify} disabled={busy} data-testid="support-otp-verify">{busy ? "Verifying…" : "Continue to documents"}</button>
          </>
        )}

        {mode === "register" && stage === "docs" && (
          <>
            <p className="lead" style={{ marginBottom: 14 }}>Upload your documents for verification (image or PDF). Your employee number is <b>{employeeNo}</b>.</p>
            {[
              { key: "identity", label: "Identity Card (Aadhaar / Govt. ID)", req: true },
              { key: "qualification_cert", label: "Qualification Certificate", req: true },
              { key: "good_standing", label: "Certificate of Good Standing", req: false },
            ].map((d) => (
              <div className="docrow" key={d.key}>
                <FileText size={18} color="#185FA5" />
                <div className="m"><h4>{d.label}<span className={`req ${d.req ? "r" : "o"}`}>{d.req ? "required" : "optional"}</span></h4>
                  <p>{files[d.key] ? files[d.key].name : "No file chosen"}</p></div>
                <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                  <Upload size={13} /> {files[d.key] ? "Change" : "Upload"}
                  <input type="file" accept="image/*,.pdf,application/pdf" hidden onChange={(e) => setFiles({ ...files, [d.key]: e.target.files[0] })} data-testid={`support-file-${d.key}`} />
                </label>
              </div>
            ))}
            <button className="btn btn-ink full" style={{ marginTop: 10 }} onClick={submitDocs} disabled={busy} data-testid="support-submit-docs">{busy ? "Submitting…" : "Submit for verification"}</button>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------- Register a patient --------------------------- */
function RegisterPatient() {
  const DEMO_PW = "Patient@123";
  const [tab, setTab] = useState("otp"); // otp | noauth
  const [stage, setStage] = useState("form"); // form | otp | done
  const [form, setForm] = useState({ name: "", age: "", sex: "", email: "", mobile: "", password: DEMO_PW });
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const switchTab = (t) => {
    setTab(t); setStage("form"); setOtp(""); setDevOtp(""); setResult(null); setErr("");
  };
  const resetAll = () => {
    setForm({ name: "", age: "", sex: "", email: "", mobile: "", password: DEMO_PW });
    setStage("form"); setOtp(""); setDevOtp(""); setResult(null); setErr("");
  };

  const generateOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/support/patient/send-otp", form);
      if (data.dev_otp) setDevOtp(data.dev_otp);
      setStage("otp");
      toast("Verification code generated — read it to the patient on call.");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/support/patient/verify-otp", { email: form.email, code: otp });
      setResult({ verified: true, patient_code: data.patient_code, name: data.name });
      setStage("done");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitNoAuth = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/support/patient/register-unverified", form);
      setResult({ pending: true, name: form.name });
      setStage("done");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = (t) => { try { navigator.clipboard.writeText(t); toast("Patient ID copied ✓"); } catch { /* ignore */ } };

  /* ---- Success screens ---- */
  if (stage === "done" && result?.verified) return (
    <div data-testid="support-register-patient">
      <h2 className="h" style={{ fontSize: 24 }}>Register a patient</h2>
      <div className="cs-result ok" data-testid="cs-patient-verified-result">
        <CheckCircle2 size={40} color="#1D9E75" />
        <h3>Patient registered &amp; verified</h3>
        <p><b>{result.name}</b> has been verified by email OTP and added to the platform.</p>
        <div className="cs-pid">
          <span className="cs-pid-label">Unique Patient ID</span>
          <span className="cs-pid-chip" data-testid="cs-assigned-pid">{result.patient_code}</span>
          <button className="pid-mini" title="Copy" onClick={() => copy(result.patient_code)} data-testid="cs-copy-pid"><Copy size={13} /></button>
        </div>
        <p className="note">Demo sign-in password: <b>{form.password}</b> — the patient can reset it anytime via “Forgot password”.</p>
        <button className="btn btn-ink" onClick={resetAll} data-testid="cs-register-another">Register another patient</button>
      </div>
    </div>
  );

  if (stage === "done" && result?.pending) return (
    <div data-testid="support-register-patient">
      <h2 className="h" style={{ fontSize: 24 }}>Register a patient</h2>
      <div className="cs-result pending" data-testid="cs-patient-pending-result">
        <ShieldAlert size={40} color="#C9821B" />
        <h3>Sent for admin approval</h3>
        <p><b>{result.name}</b> could not be email-verified, so the registration has been queued for admin approval. <b>No Patient ID is assigned yet</b> — the admin will assign one once approved.</p>
        <button className="btn btn-ink" onClick={resetAll} data-testid="cs-register-another">Register another patient</button>
      </div>
    </div>
  );

  /* ---- Shared details form ---- */
  const detailsForm = (
    <>
      <div className="field"><label>Full name</label><input className="in" value={form.name} onChange={set("name")} placeholder="Patient's full name" data-testid="cs-patient-name" /></div>
      <div className="row2">
        <div className="field"><label>Age</label><input className="in" value={form.age} onChange={set("age")} placeholder="e.g. 34" data-testid="cs-patient-age" /></div>
        <div className="field"><label>Gender</label>
          <select className="in" value={form.sex} onChange={set("sex")} data-testid="cs-patient-sex">
            <option value="">Select</option>{SEX_OPTIONS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="row2">
        <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} placeholder="patient@email.com" data-testid="cs-patient-email" /></div>
        <div className="field"><label>Phone number</label><input className="in" value={form.mobile} onChange={set("mobile")} placeholder="+91…" data-testid="cs-patient-mobile" /></div>
      </div>
      <div className="field"><label>Demo password <span style={{ color: "var(--slate)", fontWeight: 400 }}>(patient can reset later)</span></label><input className="in" value={form.password} onChange={set("password")} data-testid="cs-patient-password" /></div>
    </>
  );

  return (
    <div data-testid="support-register-patient">
      <h2 className="h" style={{ fontSize: 24 }}>Register a patient</h2>
      <p className="lead">Enter the patient's details on their behalf. Verify their email with an OTP, or queue them for admin approval if they can't.</p>

      <div className="cs-tabs">
        <button className={`cs-tab ${tab === "otp" ? "on" : ""}`} onClick={() => switchTab("otp")} data-testid="cs-tab-otp">
          <ShieldCheck size={15} /> With email OTP
        </button>
        <button className={`cs-tab ${tab === "noauth" ? "on" : ""}`} onClick={() => switchTab("noauth")} data-testid="cs-tab-noauth">
          <ShieldAlert size={15} /> Without OTP (admin approval)
        </button>
      </div>

      <div className="cs-form-card">
        {err && <p className="err" data-testid="cs-patient-error">{err}</p>}

        {/* ---- WITH OTP ---- */}
        {tab === "otp" && stage === "form" && (
          <>
            <div className="cs-hint"><Mail size={15} color="#185FA5" /> An OTP will be sent to the patient's email. Ask them to read it back to you, then verify below.</div>
            {detailsForm}
            <button className="btn btn-green full" onClick={generateOtp} disabled={busy} data-testid="cs-generate-otp">
              <KeyRound size={15} /> {busy ? "Generating…" : "Generate OTP"}
            </button>
          </>
        )}

        {tab === "otp" && stage === "otp" && (
          <>
            <div className="cs-hint"><Mail size={15} color="#185FA5" /> Code sent to <b>{form.email}</b>. Enter the OTP the patient reads out to you.</div>
            {devOtp && <div className="demohint" data-testid="cs-dev-otp">Demo mode — the patient's code is <b>{devOtp}</b></div>}
            <div className="field"><label>Email OTP</label><input className="in" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="6-digit code" data-testid="cs-otp-input" /></div>
            <div className="row2">
              <button className="btn btn-ghost" onClick={generateOtp} disabled={busy} data-testid="cs-resend-otp"><RefreshCw size={14} /> Resend</button>
              <button className="btn btn-green" onClick={verifyOtp} disabled={busy} data-testid="cs-verify-otp">{busy ? "Verifying…" : "Verify & create patient"}</button>
            </div>
            <p className="note" style={{ cursor: "pointer" }} onClick={() => switchTab("noauth")} data-testid="cs-cant-verify">Patient can't verify the OTP? Register without OTP →</p>
          </>
        )}

        {/* ---- WITHOUT OTP ---- */}
        {tab === "noauth" && (
          <>
            <div className="cs-hint warn"><ShieldAlert size={15} color="#C9821B" /> This patient won't be email-verified. The registration will be sent for <b>admin approval</b> and a Patient ID is assigned only after approval.</div>
            {detailsForm}
            <button className="btn btn-ink full" onClick={submitNoAuth} disabled={busy} data-testid="cs-submit-noauth">{busy ? "Submitting…" : "Submit for admin approval"}</button>
          </>
        )}
      </div>
    </div>
  );
}

/* --------------------------- Register a doctor --------------------------- */
function RegisterDoctor() {
  const empty = {
    name: "", email: "", mobile: "", specialty: "", reg_no: "",
    qualifications: "", experience_years: "", languages: "", consult_fee: "",
    bio: "", password: "Doctor@123",
  };
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const copy = (t) => { try { navigator.clipboard.writeText(t); toast("Copied ✓"); } catch { /* ignore */ } };

  const submit = async () => {
    setErr("");
    if (!form.name.trim()) { setErr("Doctor's full name is required."); return; }
    if (!form.email.trim()) { setErr("Email is required."); return; }
    if (!form.reg_no.trim() || !form.specialty.trim()) { setErr("NMC registration number and specialty are required."); return; }
    if ((form.password || "").length < 8) { setErr("Demo password must be at least 8 characters."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/support/doctor/register", form);
      setResult({ name: data.name, email: data.email, demo_password: data.demo_password });
      toast("Doctor registered ✓");
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (result) {
    return (
      <div data-testid="support-register-doctor">
        <h2 className="h" style={{ fontSize: 24 }}>Register a doctor</h2>
        <div className="cs-result ok" data-testid="cs-doctor-result">
          <div className="cs-result-head"><CheckCircle2 size={22} color="#1D9E75" /> <b>{result.name}</b> has been registered.</div>
          <p className="lead" style={{ marginTop: 6 }}>Share these sign-in details with the doctor:</p>
          <div className="cs-cred-row"><span><Mail size={14} /> Email</span>
            <span><b>{result.email}</b><button className="pid-mini" title="Copy" onClick={() => copy(result.email)}><Copy size={13} /></button></span>
          </div>
          <div className="cs-cred-row"><span><Lock size={14} /> Demo password</span>
            <span><b data-testid="cs-doctor-demo-pw">{result.demo_password}</b><button className="pid-mini" title="Copy" onClick={() => copy(result.demo_password)}><Copy size={13} /></button></span>
          </div>
          <div className="cs-hint" style={{ marginTop: 14 }}>
            <ShieldCheck size={15} color="#185FA5" /> On first sign-in the doctor must set their own password, then upload documents and apply for approval. They can access the full portal only after an admin approves them.
          </div>
          <button className="btn btn-ink" style={{ marginTop: 16 }} onClick={() => { setResult(null); setForm(empty); }} data-testid="cs-doctor-register-another">Register another doctor</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="support-register-doctor">
      <h2 className="h" style={{ fontSize: 24 }}>Register a doctor</h2>
      <p className="lead">Onboard a specialist on their behalf with all their details and a demo password. They&apos;ll set their own password and upload credentials on first sign-in, then await admin approval.</p>
      <div className="cs-form-card">
        {err && <p className="err" data-testid="cs-doctor-error">{err}</p>}
        <div className="cs-hint"><Stethoscope size={15} color="#185FA5" /> Enter the same information the doctor would provide when registering themselves.</div>

        <div className="field"><label>Full name</label><input className="in" value={form.name} onChange={set("name")} placeholder="Dr. …" data-testid="cs-doctor-name" /></div>
        <div className="row2">
          <div className="field"><label>Email</label><input className="in" value={form.email} onChange={set("email")} placeholder="doctor@email.com" data-testid="cs-doctor-email" /></div>
          <div className="field"><label>Phone number</label><input className="in" value={form.mobile} onChange={set("mobile")} placeholder="+91…" data-testid="cs-doctor-mobile" /></div>
        </div>
        <div className="row2">
          <div className="field"><label>NMC reg. no.</label><input className="in" value={form.reg_no} onChange={set("reg_no")} placeholder="e.g. KMC 119333" data-testid="cs-doctor-regno" /></div>
          <div className="field"><label>Specialty</label>
            <select className="in" value={form.specialty} onChange={set("specialty")} data-testid="cs-doctor-specialty">
              <option value="">Select</option>{DOC_SPECIALTIES.map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="row2">
          <div className="field"><label>Qualifications</label><input className="in" value={form.qualifications} onChange={set("qualifications")} placeholder="e.g. MBBS, MD, DM Cardiology" data-testid="cs-doctor-qualifications" /></div>
          <div className="field"><label>Experience (years)</label><input className="in" value={form.experience_years} onChange={set("experience_years")} placeholder="e.g. 12" data-testid="cs-doctor-experience" /></div>
        </div>
        <div className="row2">
          <div className="field"><label>Languages</label><input className="in" value={form.languages} onChange={set("languages")} placeholder="e.g. English, Hindi, Telugu" data-testid="cs-doctor-languages" /></div>
          <div className="field"><label>Consultation fee (₹)</label><input className="in" value={form.consult_fee} onChange={set("consult_fee")} placeholder="e.g. 999" data-testid="cs-doctor-fee" /></div>
        </div>
        <div className="field"><label>Short bio</label><textarea className="in" rows={3} value={form.bio} onChange={set("bio")} placeholder="A brief professional summary patients will see." data-testid="cs-doctor-bio" /></div>
        <div className="field"><label>Demo password <span style={{ color: "var(--slate)", fontWeight: 400 }}>(doctor sets their own on first login)</span></label><input className="in" value={form.password} onChange={set("password")} data-testid="cs-doctor-password" /></div>

        <button className="btn btn-green full" onClick={submit} disabled={busy} data-testid="cs-doctor-submit">
          <KeyRound size={15} /> {busy ? "Registering…" : "Register doctor"}
        </button>
      </div>
    </div>
  );
}

/* ------------------------- Book an appointment (CS) ---------------------- */
const NOTES_MAX_WORDS = 10;

function BookForPatient({ patient, phoneVisible, onBack }) {
  const [step, setStep] = useState(1);
  const [complaint, setComplaint] = useState("");
  const [need, setNeed] = useState("Video consultation");
  const [specialty, setSpecialty] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [doctor, setDoctor] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/public/specialties").then((r) => setSpecialties(r.data.specialties || [])); }, []);
  useEffect(() => { if (specialty) api.get(`/public/doctors?specialty=${encodeURIComponent(specialty)}`).then((r) => setDoctors(r.data.doctors || [])); }, [specialty]);
  useEffect(() => { if (doctor && date) api.get(`/availability?doctor_id=${doctor.id}&date=${date}`).then((r) => setSlots(r.data.slots || [])); }, [doctor, date]);

  const words = notes.trim() ? notes.trim().split(/\s+/) : [];
  const tooManyWords = words.length > NOTES_MAX_WORDS;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/support/appointments", {
        patient_id: patient.id, specialty, doctor_id: doctor.id, date, time,
        consult_type: need, symptom: complaint, notes,
      });
      setDone(data.appointment);
      toast("Appointment booked ✓");
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="support-book-appointment">
        <h2 className="h" style={{ fontSize: 24 }}>Book an appointment</h2>
        <div className="cs-result ok" data-testid="cs-book-success">
          <div className="cs-result-head"><CheckCircle2 size={22} color="#1D9E75" /> Appointment confirmed</div>
          <div className="cs-cred-row"><span><UserRound size={14} /> Patient</span><span><b>{done.patient_name}</b> · {patient.patient_code}</span></div>
          <div className="cs-cred-row"><span><Stethoscope size={14} /> Specialist</span><span><b>{done.doctor_name}</b> · {done.specialty}</span></div>
          <div className="cs-cred-row"><span><Clock size={14} /> When</span><span><b>{done.date}</b> at <b>{done.time}</b> · {done.consult_type}</span></div>
          <button className="btn btn-ink" style={{ marginTop: 16 }} onClick={onBack} data-testid="cs-book-done">Back to search</button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="support-book-appointment">
      <button className="back" onClick={onBack} data-testid="cs-book-back"><ArrowLeft size={15} /> Back to search</button>
      <h2 className="h" style={{ fontSize: 24 }}>Book for {patient.name}</h2>
      <div className="cs-book-patient" data-testid="cs-book-patient">
        <span className="pid-chip">{patient.patient_code}</span>
        <small><Mail size={11} /> {patient.email || "—"}</small>
        {phoneVisible && patient.phone !== undefined && <small><Phone size={11} /> {patient.phone || "—"}</small>}
      </div>

      <div className="psteps" style={{ margin: "14px 0" }}>
        {[1, 2, 3].map((s) => <div key={s} className={`pstep ${step > s ? "done" : step === s ? "now" : ""}`} />)}
      </div>

      {step === 1 && (
        <div className="cs-form-card">
          <div className="field"><label>What brings the patient here?</label>
            <select className="in" value={complaint} onChange={(e) => setComplaint(e.target.value)} data-testid="cs-book-complaint">
              <option value="">Select main concern</option>{COMPLAINTS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="field"><label>What do they need?</label>
            <select className="in" value={need} onChange={(e) => setNeed(e.target.value)} data-testid="cs-book-need">
              {NEEDS.map((n) => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div className="field"><label>Specialty</label>
            <select className="in" value={specialty} onChange={(e) => { setSpecialty(e.target.value); setDoctor(null); }} data-testid="cs-book-specialty">
              <option value="">Select a specialty</option>{specialties.map((s) => <option key={s.id}>{s.name}</option>)}
            </select>
          </div>
          {specialty && (
            <div style={{ marginTop: 8 }}>
              <label style={{ fontSize: 12.5, color: "var(--slate)", display: "block", marginBottom: 8 }}>Choose the specialist</label>
              {doctors.map((d) => (
                <div className="docrow" key={d.id} onClick={() => d.bookable !== false && setDoctor(d)}
                  style={{ cursor: d.bookable === false ? "not-allowed" : "pointer", opacity: d.bookable === false ? 0.6 : 1, borderColor: doctor?.id === d.id ? "var(--pine)" : "var(--line)" }}
                  data-testid={`cs-book-doctor-${d.id}`}>
                  <div className="ic" style={{ width: 38, height: 38, borderRadius: 11 }}><Stethoscope size={18} /></div>
                  <div className="m"><h4>{d.name}</h4><p>{d.specialty} · {d.reg_no}{d.bookable === false ? ` · opens ${d.opens_at_label || "soon"}` : ""}</p></div>
                  {doctor?.id === d.id && <Check size={18} color="#1D9E75" />}
                </div>
              ))}
            </div>
          )}
          <button className="btn btn-ink full" style={{ marginTop: 14 }} disabled={!complaint || !specialty || !doctor} onClick={() => setStep(2)} data-testid="cs-book-next-1">
            Continue <ArrowRight size={15} />
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="cs-form-card">
          <Calendar selected={date} onSelect={setDate} />
          {date && (
            <div className="slots" data-testid="cs-book-slots">
              {slots.map((s) => (
                <button key={s.time} className={`slot ${time === s.time ? "sel" : ""} ${!s.available ? "taken" : ""}`}
                  disabled={!s.available} onClick={() => setTime(s.time)} data-testid={`cs-book-slot-${s.time}`}>{s.time}</button>
              ))}
            </div>
          )}
          {date && slots.length > 0 && slots.every((s) => !s.available) && (
            <p className="slots-hint slots-hint-empty">No slots available for this date — appointments must be at least 4 hours ahead.</p>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button className="btn btn-ghost" onClick={() => setStep(1)}>Back</button>
            <button className="btn btn-ink full" disabled={!date || !time} onClick={() => setStep(3)} data-testid="cs-book-next-2">Continue <ArrowRight size={15} /></button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="cs-form-card">
          <h3 className="h" style={{ fontSize: 18, marginTop: 0 }}>Review & confirm</h3>
          <div className="rrow"><span>Patient</span><span>{patient.name} · {patient.patient_code}</span></div>
          <div className="rrow"><span>Specialist</span><span>{doctor.name}</span></div>
          <div className="rrow"><span>Specialty</span><span>{specialty}</span></div>
          <div className="rrow"><span>Type</span><span>{need}</span></div>
          <div className="rrow"><span>When</span><span>{date} at {time}</span></div>
          <div className="rrow"><span>Concern</span><span>{complaint}</span></div>
          <div className="field" style={{ marginTop: 14 }}>
            <label>Notes — patient-described symptoms <span style={{ color: "var(--slate)", fontWeight: 400 }}>(optional · max {NOTES_MAX_WORDS} words)</span></label>
            <textarea className="in" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. mild chest pain since morning" data-testid="cs-book-notes" />
            <small style={{ color: tooManyWords ? "var(--lred)" : "var(--slate)", fontSize: 12 }} data-testid="cs-book-wordcount">
              {words.length}/{NOTES_MAX_WORDS} words{tooManyWords ? " — please shorten" : ""}
            </small>
          </div>
          <p className="cs-privacy-hint" style={{ marginTop: 6 }}><ShieldCheck size={13} /> Notes are optional — never insist the patient share health information.</p>
          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button className="btn btn-ghost" onClick={() => setStep(2)}>Back</button>
            <button className="btn btn-green full" onClick={confirm} disabled={busy || tooManyWords} data-testid="cs-book-confirm">
              <Check size={16} /> {busy ? "Booking…" : "Confirm appointment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function BookAppointment() {
  const [field, setField] = useState("email");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [phoneVisible, setPhoneVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [bookFor, setBookFor] = useState(null);

  const FIELDS = [
    { key: "email", label: "Email ID", icon: Mail, ph: "patient@email.com" },
    { key: "phone", label: "Phone number", icon: Phone, ph: "+91 98765 43210" },
    { key: "patient_id", label: "Patient ID", icon: IdCard, ph: "2nd-0000-0000-0001" },
  ];
  const active = FIELDS.find((f) => f.key === field);

  const search = async () => {
    setErr("");
    if (!query.trim()) { setErr("Please enter a value to search."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/support/patient/search", { query: query.trim(), field });
      setResults(data.patients);
      setPhoneVisible(data.phone_visible);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (bookFor) return <BookForPatient patient={bookFor} phoneVisible={phoneVisible} onBack={() => setBookFor(null)} />;

  return (
    <div data-testid="support-book-appointment">
      <h2 className="h" style={{ fontSize: 24 }}>Book an appointment</h2>
      <p className="lead">Search for a registered patient using a detail they share on the call — their email, phone number or Patient ID — then book a consultation for them.</p>

      <div className="cs-form-card">
        <div className="cs-search-tabs" data-testid="cs-search-tabs">
          {FIELDS.map((f) => {
            const Icon = f.icon;
            return (
              <button key={f.key} className={`cs-tab ${field === f.key ? "on" : ""}`}
                onClick={() => { setField(f.key); setResults(null); setErr(""); setQuery(""); }}
                data-testid={`cs-search-by-${f.key}`}>
                <Icon size={14} /> {f.label}
              </button>
            );
          })}
        </div>
        <div className="cs-search-row">
          {/* autoComplete off + no datalist: deliberately no live suggestions */}
          <input className="in" value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()} placeholder={active.ph}
            autoComplete="off" data-testid="cs-search-input" />
          <button className="btn btn-ink" onClick={search} disabled={busy} data-testid="cs-search-btn">
            <Search size={15} /> {busy ? "Searching…" : "Search"}
          </button>
        </div>
        {err && <p className="err" data-testid="cs-search-error">{err}</p>}
        <div className="cs-privacy-hint"><ShieldCheck size={13} /> You only see what the patient shares. Phone numbers appear only when you search by phone.</div>
      </div>

      {results !== null && (
        <div className="cs-results" data-testid="cs-search-results">
          {results.length === 0
            ? <p className="lead" data-testid="cs-search-empty">No registered patient found for that {active.label.toLowerCase()}.</p>
            : <p className="lead">{results.length} patient{results.length > 1 ? "s" : ""} found. Choose who to book for:</p>}
          {results.map((p) => (
            <div className="cs-result-row" key={p.id} data-testid={`cs-patient-${p.id}`}>
              <span className="ib-avatar patient"><UserRound size={16} /></span>
              <div className="cs-result-main">
                <b>{p.name} {p.relationship && p.relationship !== "Primary account" && <span className="rel-chip">{p.relationship}</span>}</b>
                <span className="cs-result-meta">
                  <span className="pid-chip">{p.patient_code}</span>
                  <small><Mail size={11} /> {p.email || "—"}</small>
                  {phoneVisible && p.phone !== undefined && <small data-testid={`cs-patient-phone-${p.id}`}><Phone size={11} /> {p.phone || "—"}</small>}
                </span>
              </div>
              <button className="btn btn-green btn-sm" onClick={() => setBookFor(p)} data-testid={`cs-book-${p.id}`}>
                <CalendarPlus size={13} /> Book consultation
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Communications: Mail ------------------------ */
function fmtDay(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}

function SupportMail() {
  const { tick } = useMessaging();
  const [kind, setKind] = useState("patient");     // patient | doctor | admin
  const [box, setBox] = useState("inbox");          // inbox | sent
  const [q, setQ] = useState("");
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/support/tickets", { params: { kind, box, q: q.trim() } });
      setTickets(data.tickets || []);
    } catch { setTickets([]); }
    finally { setLoading(false); }
  }, [kind, box, q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { load(); }, [tick, load]);

  // initiate admin conversation (Admin tab only)
  const startAdminConv = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/conversations/start", { target: "admin" });
      const conv = data.conversation;
      setActive({
        id: conv.id,
        name: "Platform admin",
        ticket_code: "",
        subject: "Internal conversation",
        id_label: "",
        status: "",
      });
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setStarting(false); }
  };

  const KindIcon = kind === "patient" ? UserRound : kind === "doctor" ? Stethoscope : ShieldCheck;
  const placeholder = kind === "patient"
    ? "Search by patient name, email, phone or Patient ID…"
    : kind === "doctor"
      ? "Search by doctor name, email, phone or NMC…"
      : "Search admin conversation…";

  return (
    <div data-testid="support-comm-mail">
      <h2 className="h" style={{ fontSize: 24, marginBottom: 4 }}>
        <Ticket size={20} style={{ verticalAlign: "-3px", marginRight: 8 }} />Mail
      </h2>
      <p className="lead" style={{ marginBottom: 14 }}>
        Categorised communication received by the customer support portal.
        {kind !== "admin" && " You can only reply — never initiate — patient or doctor threads."}
        {kind === "admin" && " You can start internal threads with the admin from here."}
      </p>

      <div className="panel" data-testid="support-mail-panel">
        {/* category tabs: patient / doctor / admin */}
        <div className="tkt-subtabs" data-testid="support-mail-kind-tabs">
          <button className={`tkt-subtab ${kind === "patient" ? "on" : ""}`}
            onClick={() => { setKind("patient"); setActive(null); }} data-testid="support-mail-kind-patient">
            <UserRound size={15} /> Patient
          </button>
          <button className={`tkt-subtab ${kind === "doctor" ? "on" : ""}`}
            onClick={() => { setKind("doctor"); setActive(null); }} data-testid="support-mail-kind-doctor">
            <Stethoscope size={15} /> Doctor
          </button>
          <button className={`tkt-subtab ${kind === "admin" ? "on" : ""}`}
            onClick={() => { setKind("admin"); setActive(null); }} data-testid="support-mail-kind-admin">
            <ShieldCheck size={15} /> Admin
          </button>
        </div>

        {active ? (
          <TicketThread
            ticket={active}
            kind={kind}
            viewer="support"
            onBack={() => { setActive(null); load(); }}
            onSent={load}
          />
        ) : (
          <>
            {/* box: inbox / sent */}
            <div className="tkt-boxtabs" data-testid="support-mail-box-tabs">
              <button className={`tkt-boxtab ${box === "inbox" ? "on" : ""}`} onClick={() => setBox("inbox")} data-testid="support-mail-box-inbox">
                <InboxIcon size={14} /> Inbox
              </button>
              <button className={`tkt-boxtab ${box === "sent" ? "on" : ""}`} onClick={() => setBox("sent")} data-testid="support-mail-box-sent">
                <SendHorizonal size={14} /> Sent
              </button>
            </div>

            <div className="patient-search-bar" style={{ maxWidth: 640, marginBottom: 14 }}>
              <Search size={15} className="patient-search-ic" />
              <input
                className="patient-search-input"
                placeholder={placeholder}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="support-mail-search-input"
              />
              {q && <button className="patient-search-clear" onClick={() => setQ("")} data-testid="support-mail-search-clear">✕</button>}
            </div>

            {/* "Message admin" CTA — only on Admin tab when none exists */}
            {kind === "admin" && tickets.length === 0 && !loading && (
              <div style={{ marginBottom: 14 }}>
                <button className="btn btn-ink" onClick={startAdminConv} disabled={starting} data-testid="support-mail-start-admin">
                  <Send size={14} /> {starting ? "Opening…" : "Message admin"}
                </button>
              </div>
            )}

            <div className="tkt-list" data-testid="support-mail-list">
              {loading && <p className="sub" style={{ padding: "18px 4px" }}>Loading…</p>}
              {!loading && tickets.length === 0 && kind !== "admin" && (
                <p className="tkt-empty" data-testid="support-mail-empty">
                  <MessageSquare size={26} /><br />
                  {q
                    ? `No ${kind} conversations match “${q}”.`
                    : box === "inbox"
                      ? `No ${kind} messages have come in yet.`
                      : `You haven't sent any ${kind} replies yet.`}
                </p>
              )}
              {!loading && tickets.length === 0 && kind === "admin" && (
                <p className="tkt-empty" data-testid="support-mail-empty">
                  <ShieldQuestion size={26} /><br />
                  No internal conversation with admin yet. Click <b>Message admin</b> to start one.
                </p>
              )}
              {!loading && tickets.map((t) => (
                <button key={t.id} className={`tkt-row ${t.unread > 0 ? "unread" : ""}`}
                  onClick={() => setActive(t)} data-testid={`support-mail-row-${t.id}`}>
                  <span className={`ib-avatar ${kind === "admin" ? "admin" : kind}`}><KindIcon size={16} /></span>
                  <span className="tkt-row-main">
                    <span className="tkt-row-top">
                      {t.ticket_code && <span className="tkt-code">{t.ticket_code}</span>}
                      <b className="tkt-row-name">{t.name}</b>
                      <time>{fmtDay(t.last_at)}</time>
                    </span>
                    <span className="tkt-row-subject">{t.subject}</span>
                    <span className="tkt-row-meta">
                      {t.id_label && <span className="pid-chip sm">{t.id_label}</span>}
                      {t.email && <span className="tkt-row-contact">{t.email}</span>}
                      {t.phone && <span className="tkt-row-contact">{t.phone}</span>}
                    </span>
                    <span className="tkt-row-prev">
                      {t.last_sender_role === "support" ? <span className="tkt-prev-tag">You: </span> : null}
                      {t.last_text || "No messages yet"}
                    </span>
                  </span>
                  {t.unread > 0 && <span className="ib-unread">{t.unread}</span>}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------ Communications: Callbacks ---------------------- */
function SupportCallbacks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get("/support/callbacks").then((r) => setItems(r.data.callbacks || [])).catch(() => setItems([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const attend = async (id) => {
    try { await api.post(`/support/callbacks/${id}/attend`); toast("Marked attended ✓"); load(); }
    catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="support-comm-callbacks">
      <h2 className="h" style={{ fontSize: 24 }}>Callbacks</h2>
      <p className="lead">Patients who asked to be called back. They shared their number for this purpose.</p>
      {loading ? <p className="lead">Loading…</p> : items.length === 0 ? (
        <div className="support-soon"><PhoneCall size={26} color="#185FA5" /><div><h3>No callbacks</h3><p>New callback requests will land here.</p></div></div>
      ) : (
        <div className="cs-results">
          {items.map((c) => (
            <div className="cs-result-row" key={c.id} data-testid={`cs-callback-${c.id}`}>
              <span className="ib-avatar patient"><PhoneCall size={16} /></span>
              <div className="cs-result-main">
                <b>{c.name || "Patient"} {c.status === "attended" && <span className="rel-chip">Attended</span>}</b>
                <span className="cs-result-meta">
                  <small><Phone size={11} /> {c.phone || "—"}</small>
                  {c.service && <small><Stethoscope size={11} /> {c.service}</small>}
                  {c.preferred_datetime && <small><Clock size={11} /> {c.preferred_datetime}</small>}
                </span>
                {c.note && <small style={{ color: "var(--slate)" }}>“{c.note}”</small>}
                {c.status === "attended" && c.attended_by_name && <small style={{ color: "var(--slate)" }}>Attended by {c.attended_by_name}</small>}
              </div>
              {c.status !== "attended" && (
                <button className="btn btn-green btn-sm" onClick={() => attend(c.id)} data-testid={`cs-callback-attend-${c.id}`}>
                  <CheckCheck size={13} /> Mark attended
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------- Support workspace --------------------------- */
/* --------------------------- Support email inbox ------------------------- */
function SupportEmailInbox() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const loadThreads = () =>
    api.get("/support/email/threads").then((r) => setThreads(r.data.threads || [])).catch(() => {}).finally(() => setLoading(false));

  useEffect(() => {
    loadThreads();
    const t = setInterval(loadThreads, 30000);
    return () => clearInterval(t);
  }, []);

  const openThread = async (tid) => {
    setSel(tid);
    setMsgs([]);
    try {
      const r = await api.get(`/support/email/threads/${tid}`);
      setMsgs(r.data.messages || []);
      loadThreads();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  };

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/support/email/threads/${sel}/reply`, { body: reply });
      setReply("");
      toast("Reply sent ✓");
      await openThread(sel);
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div data-testid="support-email-inbox">
      <h2 className="h" style={{ fontSize: 24, marginBottom: 4 }}>Email inbox</h2>
      <p className="lead" style={{ marginBottom: 14 }}>
        Emails patients send to <b>support@2ndopinionconsult.in</b> land here. Reply and the patient
        gets your message from support@ — their answer comes straight back into this conversation.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "start" }}>
        <div className="card" style={{ padding: 0, maxHeight: 560, overflowY: "auto" }} data-testid="email-thread-list">
          {loading && <div style={{ padding: 18 }} className="note">Loading…</div>}
          {!loading && threads.length === 0 && (
            <div className="support-soon" style={{ margin: 12 }}>
              <AtSign size={24} color="#185FA5" />
              <div><h3>No emails yet</h3><p>Patient emails to support@ will appear here.</p></div>
            </div>
          )}
          {threads.map((t) => (
            <button
              key={t.thread_id}
              onClick={() => openThread(t.thread_id)}
              data-testid={`email-thread-${t.thread_id}`}
              style={{
                display: "block", width: "100%", textAlign: "left", padding: "12px 14px",
                border: "none", borderBottom: "1px solid var(--line)", cursor: "pointer",
                background: sel === t.thread_id ? "var(--mint-soft)" : "transparent",
              }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <b style={{ fontSize: 13.5, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {t.last_from_name || t.counterparty}
                </b>
                {t.unread > 0 && <span className="pill" style={{ background: "#185FA5", color: "#fff", fontSize: 11, flexShrink: 0 }} aria-label={`${t.unread} unread message${t.unread > 1 ? "s" : ""}`} data-testid={`email-unread-${t.thread_id}`}>{t.unread}</span>}
              </div>
              <div style={{ fontSize: 12.5, color: "var(--slate)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.subject}</div>
              <div style={{ fontSize: 11.5, color: "var(--slate)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 2 }}>
                {t.last_direction === "out" ? "You: " : ""}{(t.last_body || "").slice(0, 60)}
              </div>
            </button>
          ))}
        </div>

        <div className="card" style={{ minHeight: 360 }}>
          {!sel && <p className="note" style={{ padding: 20, textAlign: "center" }}>Select a conversation to read and reply.</p>}
          {sel && (
            <>
              <div style={{ maxHeight: 430, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingRight: 4 }} data-testid="email-thread-messages">
                {msgs.map((m) => (
                  <div key={m.id || m.message_id} style={{ display: "flex", justifyContent: m.direction === "out" ? "flex-end" : "flex-start" }}>
                    <div style={{
                      maxWidth: "80%", padding: "10px 12px", borderRadius: 12,
                      background: m.direction === "out" ? "#185FA5" : "var(--porcelain)",
                      color: m.direction === "out" ? "#fff" : "var(--ink)",
                    }}>
                      <div style={{ fontSize: 11, opacity: 0.75, marginBottom: 3 }}>
                        {m.direction === "out" ? "Support" : (m.from_name || m.from_addr)} · {fmt(m.created_at)}
                      </div>
                      <div style={{ fontSize: 13.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.body_text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12, borderTop: "1px solid var(--line)", paddingTop: 12, alignItems: "flex-end" }}>
                <textarea
                  className="in" rows={2} value={reply} onChange={(e) => setReply(e.target.value)}
                  placeholder="Type your reply… (sent from support@2ndopinionconsult.in)"
                  data-testid="email-reply-input" style={{ flex: 1, minWidth: 0, resize: "vertical" }} />
                <button className="btn btn-ink" onClick={send} disabled={busy || !reply.trim()} data-testid="email-reply-send" style={{ flexShrink: 0, whiteSpace: "nowrap" }}>
                  <Send size={15} /> {busy ? "Sending…" : "Reply"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------- Workspace ------------------------------- */
function SupportWorkspace({ user }) {
  const [page, setPage] = useState("dashboard");
  const NAV = [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "register-patient", label: "Register a patient", icon: UserPlus },
    { key: "register-doctor", label: "Register a doctor", icon: Stethoscope },
    { key: "book-appointment", label: "Book an appointment", icon: CalendarPlus },
    { key: "comm-inbox", label: "Inbox", icon: InboxIcon, group: "Communications" },
    { key: "comm-mail", label: "Mail", icon: Mail, group: "Communications" },
    { key: "comm-callbacks", label: "Callbacks", icon: PhoneCall, group: "Communications" },
    { key: "comm-email", label: "Email inbox", icon: AtSign, group: "Communications" },
    { key: "profile", label: "My profile", icon: UserRound },
  ];
  return (
    <div className="support-layout">
      <aside className="support-side" data-testid="support-sidebar">
        <div className="support-emp">
          <span className="support-emp-no" data-testid="support-emp-no">{user.employee_no}</span>
          <b>{user.name}</b>
          <small>Customer Support</small>
        </div>
        <nav>
          {NAV.map((n, i) => {
            const Icon = n.icon;
            const showGroup = n.group && (i === 0 || NAV[i - 1].group !== n.group);
            return (
              <div key={n.key}>
                {showGroup && <div className="support-nav-group" data-testid={`support-navgroup-${n.group}`}>{n.group}</div>}
                <button className={`support-nav ${page === n.key ? "on" : ""}`} onClick={() => setPage(n.key)} data-testid={`support-nav-${n.key}`}>
                  <Icon size={16} /> {n.label}
                </button>
              </div>
            );
          })}
        </nav>
      </aside>

      <main className="support-main">
        {page === "dashboard" && (
          <div data-testid="support-dashboard">
            <h2 className="h" style={{ fontSize: 24 }}>Welcome, {user.name.split(" ")[0]} 👋</h2>
            <p className="lead">Your customer support workspace. Tools to help patients and doctors will appear here soon.</p>
            <div className="support-cards">
              <div className="support-card"><BadgeCheck size={20} color="#1D9E75" /><div><h4>Approved</h4><p>Employee {user.employee_no}</p></div></div>
              <div className="support-card"><GraduationCap size={20} color="#185FA5" /><div><h4>Qualification</h4><p>{user.qualification || "—"}</p></div></div>
              <div className="support-card"><LanguagesIcon size={20} color="#7A5AF8" /><div><h4>Languages</h4><p>{(user.languages || []).join(", ") || "—"}</p></div></div>
            </div>
            <div className="support-soon" data-testid="support-coming-soon">
              <Headset size={26} color="#185FA5" />
              <div><h3>More tools coming soon</h3><p>Your team will configure what you can access next. Hang tight!</p></div>
            </div>
          </div>
        )}

        {page === "register-patient" && <RegisterPatient />}

        {page === "register-doctor" && <RegisterDoctor />}

        {page === "book-appointment" && <BookAppointment />}

        {page === "comm-inbox" && (
          <div data-testid="support-comm-inbox">
            <h2 className="h" style={{ fontSize: 24, marginBottom: 4 }}>Inbox</h2>
            <p className="lead" style={{ marginBottom: 14 }}>Patient &amp; doctor tickets land here first. You see only their name and ID — escalate to admin if you can’t resolve it.</p>
            <div className="support-inbox-wrap"><Inbox role="support" accent="#185FA5" /></div>
          </div>
        )}

        {page === "comm-mail" && <SupportMail />}

        {page === "comm-callbacks" && <SupportCallbacks />}

        {page === "comm-email" && <SupportEmailInbox />}

        {page === "profile" && (
          <div data-testid="support-profile">
            <h2 className="h" style={{ fontSize: 24 }}>My profile</h2>
            <div className="support-profile-grid">
              {[
                ["Employee number", user.employee_no],
                ["Name (as per Aadhaar)", user.name],
                ["Age", user.age],
                ["Gender", user.sex],
                ["Qualification", user.qualification],
                ["Languages", (user.languages || []).join(", ")],
                ["Email", user.email],
                ["Phone", user.mobile],
              ].map(([k, v]) => (
                <div className="support-pf" key={k}><label>{k}</label><span>{v || "—"}</span></div>
              ))}
            </div>
            <p className="note">To update any details, please contact the admin team.</p>
          </div>
        )}
      </main>
    </div>
  );
}

/* ------------------------------- Page shell ------------------------------ */
export default function SupportPortal() {
  const { user, logout } = useAuth();

  if (user === undefined) return (
    <div className="portal"><div className="wrap"><p className="lead" style={{ padding: 40 }}>Loading…</p></div></div>
  );

  if (!user || user.role !== "support") {
    return (
      <PortalShell ribbon="Customer Support — staff access" title="Customer Support" sub="2nd Opinion Health Consultants">
        <SupportAuth onDone={() => { }} />
      </PortalShell>
    );
  }

  return (
    <PortalShell
      ribbon="Customer Support workspace"
      title="Customer Support"
      sub={user.name}
      user={user}
      onSignOut={logout}
    >
      <SupportWorkspace user={user} />
    </PortalShell>
  );
}
